import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useApp } from "@/context/AppContext";
import { usePostChannel } from "@/lib/postChannel";
import { emitPostPatch } from "@/lib/postSync";
import { defaultAvatar } from "@/lib/defaults";
import { REACTION_EMOJI } from "@/components/LiveReactions";
import { imageVariant } from "@/lib/mediaUrl";

const LIFETIME_MS = 3000;
const MAX_ON_SCREEN = 10;

// Réactions EN DIRECT sur une publication ouverte (visionneuse plein écran, fiche « Publication de … »,
// théâtre PC) : les compteurs de toutes les barres de réactions se mettent à jour instantanément et la
// réaction d'un autre membre « s'envole » depuis le bas de l'écran avec son avatar (façon direct).
// Une seule instance par écran ouvert (la feuille de commentaires intégrée n'en monte pas).
export const PostLiveReactions = ({ post }) => {
  const { user } = useApp();
  const [bursts, setBursts] = useState([]);
  const seq = useRef(0);

  // Point de départ : la barre de réactions visible de la publication (fiche PC, visionneuse, théâtre) ;
  // à défaut, le bas de l'écran.
  const anchor = () => {
    const bars = Array.from(document.querySelectorAll(`[data-testid="reaction-bar-${post.id}"]`));
    const el = bars.find((b) => { const r = b.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight; });
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { left: r.left + r.width * (0.55 + Math.random() * 0.35), bottom: window.innerHeight - r.top + 4 };
  };

  usePostChannel(post?.id, (msg) => {
    if (msg.type !== "post_reaction" || !msg.counts) return;
    emitPostPatch(post.id, { reactions: msg.counts });
    if (!msg.reaction || !msg.user || msg.user.id === user?.id) return;
    const id = ++seq.current;
    const a = anchor();
    const b = {
      id,
      emoji: REACTION_EMOJI[msg.reaction] || "👍",
      name: msg.user.name || "",
      avatar: msg.user.avatar_url ? imageVariant(msg.user.avatar_url, 48) : defaultAvatar(msg.user.gender),
      left: a ? `${Math.round(a.left)}px` : `min(calc(50% + ${60 + Math.round(Math.random() * 180)}px), 86%)`,
      bottom: a ? `${Math.round(a.bottom)}px` : "calc(var(--sa-bottom, 0px) + 8rem)",
      drift: Math.round((Math.random() * 2 - 1) * 50),
      rot: Math.round((Math.random() * 2 - 1) * 24),
    };
    setBursts((list) => [...list.slice(-(MAX_ON_SCREEN - 1)), b]);
    setTimeout(() => setBursts((list) => list.filter((x) => x.id !== id)), LIFETIME_MS);
  });

  useEffect(() => () => setBursts([]), [post?.id]);

  if (bursts.length === 0) return null;
  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[120]" aria-live="polite" data-testid={`live-reactions-${post.id}`}>
      {bursts.map((b) => (
        <span
          key={b.id}
          className="live-float absolute inline-flex flex-col items-center select-none"
          style={{ left: b.left, bottom: b.bottom, fontSize: "34px", "--drift": `${b.drift}px`, "--rot": `${b.rot}deg` }}
          title={b.name}
          data-testid="live-reaction-burst"
        >
          {b.emoji}
          <img src={b.avatar} alt="" className="h-6 w-6 rounded-full object-cover ring-2 ring-white -mt-1" loading="lazy" />
        </span>
      ))}
    </div>,
    document.body
  );
};

export default PostLiveReactions;
